package co.torvus.web.sections

import co.torvus.web.contactScope
import co.torvus.web.submitContactMessage
import js.reflect.unsafeCast
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.form
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.section
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import react.useRef
import react.useState
import web.cssom.ClassName
import web.dom.ElementId
import web.html.HTMLInputElement
import web.html.HTMLTextAreaElement

private const val IDLE = "idle"
private const val SUBMITTING = "submitting"
private const val SUCCESS = "success"
private const val ERROR = "error"

val ContactSection = FC<Props> {
    var status by useState(IDLE)
    val emailRef = useRef<HTMLInputElement>()
    val subjectRef = useRef<HTMLInputElement>()
    val messageRef = useRef<HTMLTextAreaElement>()
    var canSend by useState(false)

    fun refreshCanSend() {
        val email = emailRef.current?.value?.trim().orEmpty()
        val message = messageRef.current?.value?.trim().orEmpty()
        canSend = email.contains("@") && message.isNotEmpty()
    }

    section {
        className = ClassName("contact section")
        id = ElementId("contact")

        div {
            className = ClassName("contact-inner")

            div {
                className = ClassName("contact-group")
                h2 {
                    className = ClassName("contact-heading spaced")
                    +"Contact"
                }
                div { className = ClassName("divider") }
                a {
                    className = ClassName("contact-email")
                    href = "mailto:info@torvus.co"
                    +"info@torvus.co"
                }
                a {
                    className = ClassName("contact-social")
                    href = "https://www.instagram.com/torvus.co/"
                    target = unsafeCast("_blank")
                    rel = "noopener noreferrer"
                    ariaLabel = "Torvus on Instagram"
                    span { className = ClassName("ig-icon") }
                }
            }

            div {
                className = ClassName("contact-group")
                h2 {
                    className = ClassName("contact-heading spaced")
                    +"Legal"
                }
                div { className = ClassName("divider") }
                p {
                    className = ClassName("legal-name")
                    +"Torvus"
                }
                p {
                    className = ClassName("legal-sub")
                    +"Registered in Croatia"
                }
            }

            form {
                className = ClassName("contact-form")
                onSubmit = { event ->
                    event.preventDefault()
                    val email = emailRef.current?.value?.trim().orEmpty()
                    val subject = subjectRef.current?.value?.trim().orEmpty()
                    val message = messageRef.current?.value?.trim().orEmpty()
                    if (status != SUBMITTING && email.contains("@") && message.isNotEmpty()) {
                        status = SUBMITTING
                        contactScope.launch {
                            val ok = submitContactMessage(email, subject, message)
                            if (ok) {
                                status = SUCCESS
                                emailRef.current?.value = ""
                                subjectRef.current?.value = ""
                                messageRef.current?.value = ""
                                canSend = false
                            } else {
                                status = ERROR
                            }
                        }
                    }
                }

                h2 {
                    className = ClassName("contact-heading")
                    +"Get in Touch"
                }
                div { className = ClassName("divider") }
                p {
                    className = ClassName("form-intro")
                    +"Have a question or need help? Send us a message and we'll get back to you."
                }

                div {
                    className = ClassName("field")
                    label { +"Email" }
                    input {
                        ref = emailRef
                        required = true
                        placeholder = "you@example.com"
                        onChange = { refreshCanSend() }
                    }
                }
                div {
                    className = ClassName("field")
                    label { +"Subject" }
                    input {
                        ref = subjectRef
                        placeholder = "Enter subject..."
                    }
                }
                div {
                    className = ClassName("field")
                    label { +"Message" }
                    textarea {
                        ref = messageRef
                        required = true
                        placeholder = "Write your message here..."
                        onChange = { refreshCanSend() }
                    }
                }
                button {
                    className = ClassName("send-btn")
                    disabled = status == SUBMITTING || !canSend
                    +if (status == SUBMITTING) "Sending…" else "Send Message"
                }

                if (status == SUCCESS) {
                    p {
                        className = ClassName("form-status success")
                        +("Thanks! Your message has been recorded — we've emailed you a confirmation " +
                            "and will get back to you.")
                    }
                }
                if (status == ERROR) {
                    p {
                        className = ClassName("form-status error")
                        +"Something went wrong. Please try again, or email info@torvus.co directly."
                    }
                }
            }

            p {
                className = ClassName("copyright")
                +"© 2026 Torvus"
            }
        }
    }
}
